/**
 * AetherLite Plan model — port idiomático de CoachKit/plan-model.js a TypeScript.
 *
 * Un Plan es una colección de SECCIONES MODULARES. Cada sección tiene:
 *   - id     (interno, para drag/reorder/edición)
 *   - type   (uno de los 8 tipos definidos en SECTION_ORDER)
 *   - enabled (visible o no en preview/PDF)
 *   - data   (payload tipado por sección — ver tipos *Data)
 *
 * En el backend (`aetherlite_plans.py`) el plan se serializa como:
 *   { title, client_name, data: { sections, brand, template, ... } }
 *
 * data.sections es una `List[Any]` (JSONB en Postgres) por lo que aquí
 * mantenemos shape exacta — cualquier cambio rompe vistas públicas
 * generadas con código viejo.
 */
import type { AetherLiteUser } from "../api/auth";

// ----------------------------------------------------------------------
// Tipos por sección
// ----------------------------------------------------------------------

export interface CoverData {
  eyebrow: string;
  programTitle: string;
  programSubtitle: string;
  clientName: string;
  month: string;
  duration: string;
  /** Portada visual heredada de la plantilla (C1-v2): la imagen va full-bleed
   *  detrás del contenido editorial con un overlay para contraste. Si no hay
   *  `imageUrl`, la portada usa el layout editorial clásico (solo color). */
  imageUrl?: string;
  imagePosition?: string;
  overlay?: "dark-gradient" | "color-tint" | "split" | "duotone" | "none";
}

export interface ClientData {
  fullName: string;
  birthdate: string;
  age: string;
  weight: string;
  height: string;
  goal: string;
  level: string;
  injuries: string;
  startDate: string;
  endDate: string;
}

export interface CoachBioStat {
  label: string;
  value: string;
}

export interface CoachBioData {
  bio: string;
  methodology: string;
  stats: CoachBioStat[];
  photo: string | null;
  contact: string;
}

export interface RoutineExercise {
  id: string;
  /** Si viene del catálogo, id del Exercise para deep-link / re-pick. */
  libId?: string | number;
  name: string;
  sets: number | string;
  reps: string;
  weight: string;
  rest: string;
  rir: number | string;
  notes: string;
  gifUrl?: string;
  muscle?: string;
  equipment?: string;
}

export interface RoutineDay {
  id: string;
  label: string;
  focus: string;
  warmup?: string;
  exercises: RoutineExercise[];
}

export interface RoutineWeek {
  id: string;
  label: string;
  days: RoutineDay[];
}

export interface RoutineData {
  weeks: RoutineWeek[];
}

export interface CardioSession {
  /** ID estable para que los styles[fieldPath] sobrevivan reordenamiento. Auto-gen si falta. */
  id?: string;
  day: string;
  type: string;
  duration: string;
  intensity: string;
  notes: string;
  /** Si se eligió del catálogo: id del ejercicio en exercise_library. Para
   *  mostrar el GIF en la sesión + abrir el preview con instrucciones. */
  libId?: string | number | null;
  /** URL del GIF asociado al ejercicio cardiovascular (cinta, escalador, etc.). */
  gifUrl?: string | null;
}

export interface CardioData {
  summary: string;
  sessions: CardioSession[];
}

export interface DietMacros {
  kcal: string;
  protein: string;
  carbs: string;
  fat: string;
}

/**
 * Alimento dentro de una comida (Feature 4). Los macros se guardan POR
 * `baseQty` del alimento (ej. por 100 g); el valor mostrado se escala según
 * `qty`. Así, cambiar la cantidad recalcula sin perder precisión.
 */
export interface DietFoodItem {
  id: string;
  /** Si vino del catálogo (food_library), su id — para referencia / re-pick. */
  libId?: number;
  name: string;
  /** Cantidad elegida por el coach (en `unit`). */
  qty: number;
  /** Unidad de `qty` — normalmente "g". */
  unit: string;
  /** Cantidad base a la que corresponden los macros de abajo (ej. 100). */
  baseQty: number;
  /** Macros POR `baseQty`. El display se escala por `qty / baseQty`. */
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
}

export interface DietMeal {
  /** ID estable para que los styles[fieldPath] sobrevivan reordenamiento. Auto-gen si falta. */
  id?: string;
  name: string;
  /** Texto libre — notas de la comida. En planes legacy era la lista de alimentos. */
  foods: string;
  kcal: string;
  p: string;
  c: string;
  f: string;
  /**
   * Alimentos estructurados elegidos desde el catálogo (Feature 4). Opcional:
   * planes/plantillas legacy no lo tienen y siguen funcionando con `foods` +
   * los campos de macros como texto. Cuando hay `items`, los totales kcal/p/c/f
   * de la comida se autocalculan de ellos.
   */
  items?: DietFoodItem[];
}

export interface DietDay {
  id: string;
  label: string;
  meals: DietMeal[];
}

export type DietSex = "M" | "F";
/** Mismos niveles que `ClientActivityLevel` del backend. */
export type DietActivity = "sedentary" | "light" | "moderate" | "intense" | "athlete";
export type DietAdjustMode = "maintain" | "deficit" | "surplus";
export type DietAdjustPreset = "light" | "recommended" | "aggressive" | "custom";

/**
 * Calculadora nutricional del plan (Feature). Calcula el objetivo calórico
 * (TDEE con Mifflin-St Jeor × factor de actividad, ± ajuste) y los macros
 * (proteína y grasa por kg de peso, carbos = resto). Cuando está `enabled`,
 * sus resultados se vuelcan a `DietData.macros` y se compara contra la suma de
 * los alimentos agregados.
 */
export interface DietCalculator {
  enabled: boolean;
  weightKg: number;
  heightCm: number;
  age: number;
  sex: DietSex;
  activity: DietActivity;
  /** Mantener / déficit / superávit — el signo del ajuste. */
  adjustMode: DietAdjustMode;
  /** Suave / recomendado / agresivo / personalizado. */
  adjustPreset: DietAdjustPreset;
  /** Magnitud del ajuste en % — solo se usa con `adjustPreset = "custom"`. */
  adjustPct: number;
  /** Gramos de proteína por kg de peso (el coach decide: 1.5 / 1.8 / 2 …). */
  proteinPerKg: number;
  /** Gramos de grasa por kg de peso (0.5 / 0.8 / 1 …). */
  fatPerKg: number;
}

export interface DietData {
  macros: DietMacros;
  summary: string;
  days: DietDay[];
  /** Calculadora nutricional (opcional — los planes legacy no la tienen). */
  calculator?: DietCalculator;
}

export interface SupplementItem {
  /** ID estable para que los styles[fieldPath] sobrevivan reordenamiento. Auto-gen si falta. */
  id?: string;
  name: string;
  dose: string;
  timing: string;
  notes: string;
}

export interface SupplementsData {
  summary: string;
  items: SupplementItem[];
}

export interface NotesData {
  body: string;
  signature: string;
}

export interface MeasurementRow {
  id: string;
  label: string; // "Peso", "Pecho", "Brazo contraído", etc.
  unit: string; // "kg", "cm", "%"
  values: string[]; // valores por columna (toma de medida)
}

export interface MeasurementsData {
  summary: string; // intro editable
  columns: string[]; // fechas/etiquetas de cada toma — ej ["Inicial", "Mes 1", "Mes 2"]
  rows: MeasurementRow[]; // filas de medida
}

// ----------------------------------------------------------------------
// Section discriminated union
// ----------------------------------------------------------------------

export type SectionType =
  | "cover"
  | "client"
  | "measurements"
  | "coachBio"
  | "routine"
  | "cardio"
  | "diet"
  | "supplements"
  | "notes";

/**
 * Estilos por campo dentro de una sección. Se aplican como CSS inline al
 * InlineEditable correspondiente, identificado por `fieldPath` (ej.
 * "programTitle", "weeks.0.days.0.exercises.1.name").
 *
 * Persiste en JSONB del backend — no requiere migración SQL para extender.
 */
export interface FieldStyle {
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  textAlign?: string;
  fontSize?: string;
  color?: string;
}

export interface PlanSectionBase<T extends SectionType, D> {
  id: string;
  type: T;
  enabled: boolean;
  visualOverride: any | null;
  data: D;
  /** Map fieldPath → FieldStyle. Opcional (planes legacy sin styles funcionan igual). */
  styles?: Record<string, FieldStyle>;
  /** Decoraciones flotantes tipo Canva (íconos posicionados libre en la hoja).
   *  Compartido en TODAS las secciones — sibling de `data` para no tocar cada
   *  shape específico. Ver PlanDecoration. Opcional → planes legacy sin
   *  decoraciones funcionan igual. */
  decorations?: PlanDecoration[];
}

export type PlanSection =
  | PlanSectionBase<"cover", CoverData>
  | PlanSectionBase<"client", ClientData>
  | PlanSectionBase<"measurements", MeasurementsData>
  | PlanSectionBase<"coachBio", CoachBioData>
  | PlanSectionBase<"routine", RoutineData>
  | PlanSectionBase<"cardio", CardioData>
  | PlanSectionBase<"diet", DietData>
  | PlanSectionBase<"supplements", SupplementsData>
  | PlanSectionBase<"notes", NotesData>;

// ----------------------------------------------------------------------
// Plan envelope
// ----------------------------------------------------------------------

/** Densidad visual del plan — escala el espaciado/padding de las secciones. */
export type PlanDensity = "compact" | "normal" | "spacious";

export interface PlanBrand {
  name?: string;
  tagline?: string;
  logoLetters?: string;
  logoUrl?: string | null;
  primary?: string;
  accent?: string;
  ig?: string;
  web?: string;
  phone?: string;
  /**
   * Si true Y hay `logoUrl`, cada hoja del plan renderiza una marca de agua
   * con el logo del coach (anti-plagio). Default: true cuando se construye
   * un brand con logo. El coach lo puede apagar por plan desde "Estilo" o
   * por defecto en "Mi marca".
   */
  watermark?: boolean;
  /**
   * Tema visual del plan. "dark" invierte el render de las secciones
   * (fondo oscuro + tinta clara). Lo setean las plantillas "dark editorial"
   * vía `applyDefaults`. Si falta, se asume "light" (comportamiento legacy).
   */
  theme?: "light" | "dark";
  /**
   * Tipografía del editor de preset (Feature 3). `displayFont` para títulos,
   * `bodyFont` para el cuerpo. Valores: id de fuente resuelto por `fontStack()`
   * ("bricolage" | "inter" | "playfair" | "montserrat" | "oswald" | "jetbrains").
   */
  displayFont?: string;
  bodyFont?: string;
  /** Densidad visual del plan (Feature 3). Si falta se asume "normal". */
  density?: PlanDensity;
}

/**
 * Preset de estilo reutilizable que el coach guarda en su cuenta (Feature 3).
 * Aplicarlo a un plan = copiar estos campos al `brand` del plan.
 */
export interface StylePreset {
  id: string;
  name: string;
  primary: string;
  accent: string;
  theme: "light" | "dark";
  displayFont: string;
  bodyFont: string;
  density: PlanDensity;
}

/**
 * Estructura completa que se serializa en `Plan.data` del backend.
 *
 * El backend acepta `data: any` y `client_name` como columna separada;
 * el editor lo "denormaliza" sincronizando cover.clientName ↔ client.fullName
 * ↔ Plan.client_name como conveniencia de UI.
 */
export interface PlanDoc {
  /** Identificador local (no es el id de DB; ver `Plan.id`). */
  id: string;
  name: string;
  template: string;
  brand: PlanBrand | null;
  sections: PlanSection[];
  createdAt: number;
  updatedAt: number;
}

/**
 * Decoración tipo Canva — ícono flotante posicionado libremente sobre la
 * hoja de una sección. Persiste en `PlanSection.data.decorations[]` (cada
 * sección tiene su propio array). El picker de íconos inserta acá; en el
 * editor son draggables/resizables, en readonly solo se renderizan.
 *
 * Coordenadas en PORCENTAJE (0-100) del contenedor `.al-page` así escalan
 * bien entre viewport del editor y vista pública/PDF (que pueden tener
 * tamaños distintos). El render usa `position: absolute; left: X%; top: Y%;
 * transform: translate(-50%, -50%)` para centrar en (x, y).
 */
export interface PlanDecoration {
  /** ID local estable para edición (drag/select). */
  id: string;
  /** ID del ícono en el catálogo (lib/IconLibrary.tsx → ICON_CATALOG). */
  icon: string;
  /** Posición X del CENTRO del ícono, en % del ancho de la hoja (0-100). */
  x: number;
  /** Posición Y del CENTRO del ícono, en % del alto de la hoja (0-100). */
  y: number;
  /** Tamaño del ícono en px. */
  size: number;
  /** Color del ícono. Si falta, se usa el accent del plan. */
  color?: string;
  /** Rotación en grados (-180 a 180). Si falta, 0. */
  rotation?: number;
  /** Opacidad (0-1). Si falta, 1. */
  opacity?: number;
}

// ----------------------------------------------------------------------
// Catálogo / metadata UI (labels + iconos + orden)
// ----------------------------------------------------------------------

export const SECTION_ORDER: SectionType[] = [
  "cover",
  // "Sobre el entrenador" como segunda hoja: lo primero que ve el cliente
  // después de la portada es CONOCER A SU ENTRENADOR (foto, bio, metodología,
  // contacto). Genera vínculo antes de bajar a tablas de ejercicios/comidas.
  "coachBio",
  "client",
  "measurements",
  "routine",
  "cardio",
  "diet",
  "supplements",
  "notes",
];

export const SECTION_LABELS: Record<SectionType, string> = {
  cover: "Portada",
  client: "Datos del cliente",
  measurements: "Medidas y progreso",
  coachBio: "Sobre el entrenador",
  routine: "Rutina de entrenamiento",
  cardio: "Cardio",
  diet: "Plan nutricional",
  supplements: "Suplementación",
  notes: "Notas del entrenador",
};

export const SECTION_DESC: Record<SectionType, string> = {
  cover: "Carátula con cliente, mes y branding",
  client: "Perfil: edad, peso, objetivo, lesiones",
  measurements: "Perímetros corporales y evolución",
  coachBio: "Bio del entrenador, foto, redes",
  routine: "Días, ejercicios, series, reps, peso",
  cardio: "Frecuencia, tipo, duración, intensidad",
  diet: "Macros y comidas por día",
  supplements: "Lista de suplementos y dosis",
  notes: "Observaciones, recomendaciones, firma",
};

/** Nombres de iconos para `<Icon />` (lib/Icon.tsx). */
export const SECTION_ICONS: Record<SectionType, string> = {
  cover: "book",
  client: "user",
  measurements: "trophy",
  coachBio: "user",
  routine: "hammer",
  cardio: "flame",
  diet: "gift",
  supplements: "sparkle",
  notes: "chat",
};

/** Secciones que no pueden eliminarse (sólo desactivarse). */
export const SECTION_REQUIRED: Record<SectionType, boolean> = {
  cover: true,
  client: false,
  measurements: false,
  coachBio: false,
  routine: false,
  cardio: false,
  diet: false,
  supplements: false,
  notes: false,
};

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

function rid(): string {
  return Math.random().toString(36).slice(2, 9);
}

function defaultDay(label: string, focus: string, exercises: Omit<RoutineExercise, "id">[]): RoutineDay {
  return {
    id: "d_" + rid(),
    label,
    focus,
    warmup: "5-10 min · movilidad articular + activación específica del grupo",
    exercises: exercises.map((e) => ({ id: "ex_" + rid(), ...e })),
  };
}

/** Genera el `data` por defecto para un tipo de sección. */
export function defaultSectionData(type: SectionType): PlanSection["data"] {
  switch (type) {
    case "cover":
      return {
        eyebrow: "PLAN DE ENTRENAMIENTO",
        programTitle: "",
        programSubtitle: "",
        clientName: "",
        month: "",
        duration: "",
      } as CoverData;

    case "client":
      return {
        fullName: "",
        birthdate: "",
        age: "",
        weight: "",
        height: "",
        goal: "",
        level: "",
        injuries: "",
        startDate: "",
        endDate: "",
      } as ClientData;

    case "measurements":
      return {
        summary: "",
        columns: ["Inicial"],
        rows: [
          { id: genId("mr"), label: "Peso", unit: "kg", values: [""] },
          { id: genId("mr"), label: "% Grasa", unit: "%", values: [""] },
          { id: genId("mr"), label: "Cuello", unit: "cm", values: [""] },
          { id: genId("mr"), label: "Pecho", unit: "cm", values: [""] },
          { id: genId("mr"), label: "Brazo relajado", unit: "cm", values: [""] },
          { id: genId("mr"), label: "Brazo contraído", unit: "cm", values: [""] },
          { id: genId("mr"), label: "Cintura", unit: "cm", values: [""] },
          { id: genId("mr"), label: "Cadera", unit: "cm", values: [""] },
          { id: genId("mr"), label: "Glúteo", unit: "cm", values: [""] },
          { id: genId("mr"), label: "Muslo", unit: "cm", values: [""] },
          { id: genId("mr"), label: "Pantorrilla", unit: "cm", values: [""] },
        ],
      } as MeasurementsData;

    case "coachBio":
      return {
        bio: "",
        methodology: "",
        stats: [],
        photo: null,
        contact: "",
      } as CoachBioData;

    case "routine":
      return {
        weeks: [
          {
            id: "w_" + rid(),
            label: "Semana 1",
            days: [
              defaultDay("Lunes", "", []),
              defaultDay("Martes", "", []),
              defaultDay("Miércoles", "", []),
              defaultDay("Jueves", "", []),
              defaultDay("Viernes", "", []),
              defaultDay("Sábado", "", []),
              defaultDay("Domingo", "Descanso", []),
            ],
          },
        ],
      } as RoutineData;

    case "cardio":
      return {
        summary: "",
        sessions: [],
      } as CardioData;

    case "diet": {
      // Default: una semana completa (Lun-Dom) en vacío. Le ahorra al coach
      // el paso de "agregar día(s)" para el caso típico (plan semanal). Si
      // quiere armar un solo "Día tipo" puede borrar los 6 días sobrantes
      // desde el header de cada día.
      const weekLabels = [
        "Lunes", "Martes", "Miércoles", "Jueves",
        "Viernes", "Sábado", "Domingo",
      ];
      return {
        macros: { kcal: "", protein: "", carbs: "", fat: "" },
        summary: "",
        days: weekLabels.map((label) => ({
          id: "dd_" + rid(),
          label,
          meals: [],
        })),
      } as DietData;
    }

    case "supplements":
      return {
        summary: "",
        items: [],
      } as SupplementsData;

    case "notes":
      return {
        body: "",
        signature: "",
      } as NotesData;
  }
}

export function defaultSection(type: SectionType): PlanSection {
  return {
    id: "s_" + rid(),
    type,
    enabled: true,
    visualOverride: null,
    data: defaultSectionData(type),
  } as PlanSection;
}

export interface DefaultPlanOpts {
  name?: string;
  template?: string;
  clientName?: string;
  brand?: PlanBrand | null;
}

export function defaultPlan(opts: DefaultPlanOpts = {}): PlanDoc {
  const { name = "Plan sin título", template = "hyper-classic", clientName = "", brand = null } = opts;
  const plan: PlanDoc = {
    id: "pl_" + Date.now().toString(36) + rid().slice(0, 3),
    name,
    template,
    brand,
    sections: SECTION_ORDER.map((t) => defaultSection(t)),
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  // Sincronizar nombre del cliente entre cover y client (mismo patrón que legacy)
  const cover = plan.sections[0] as PlanSectionBase<"cover", CoverData>;
  const client = plan.sections[1] as PlanSectionBase<"client", ClientData>;
  cover.data.clientName = clientName;
  client.data.fullName = clientName;
  return plan;
}

/** Devuelve los tipos de sección que NO están aún en el plan (para "agregar sección"). */
export function availableSectionTypes(plan: PlanDoc): SectionType[] {
  const used = new Set(plan.sections.map((s) => s.type));
  return SECTION_ORDER.filter((t) => !used.has(t));
}

/** Construye un PlanBrand a partir del usuario logueado. Útil al crear un plan nuevo. */
export function brandFromUser(user: AetherLiteUser | null): PlanBrand | null {
  if (!user) return null;
  return {
    name: user.name || "Tu marca",
    tagline: user.tagline || "ENTRENADOR PERSONAL",
    logoLetters: (user.brand_letters || (user.name || "AL")).slice(0, 2).toUpperCase(),
    logoUrl: user.brand_logo_url,
    primary: user.brand_color_primary || undefined,
    accent: user.brand_color_accent || undefined,
    ig: user.instagram || undefined,
    web: user.web || undefined,
    phone: user.phone || undefined,
    // Si el coach tiene logo, default watermark = ON. Es justo lo que pide la
    // mayoría (anti-plagio del PDF/link público que mandan al cliente).
    watermark: !!user.brand_logo_url,
  };
}

/**
 * Siembra (in-place) los textos de la portada del plan con los defaults que el
 * coach guardó en "Mi marca" (Feature 2).
 *
 * Pensado para llamarse SOLO al crear un plan (después de `template.applyDefaults`).
 * En ese momento el coach todavía no editó nada, así que su default de marca
 * pisa el genérico de la plantilla — esa es la precedencia deseada:
 *   default de marca > default de plantilla > fallback de la plantilla
 * Si el coach no configuró un default de marca, se respeta lo de la plantilla.
 */
export function applyBrandCoverDefaults(plan: PlanDoc, user: AetherLiteUser | null): void {
  if (!user) return;
  const cover = plan.sections.find((s) => s.type === "cover");
  if (!cover) return;
  const d = cover.data as CoverData;
  if (user.cover_eyebrow) d.eyebrow = user.cover_eyebrow;
  if (user.cover_subtitle) d.programSubtitle = user.cover_subtitle;
  if (user.cover_duration) d.duration = user.cover_duration;
}

/** Aplica un valor en un path tipo "weeks.0.days.1.exercises.2.name" (mutable). */
export function setPath(obj: any, path: string, value: any): void {
  const keys = path.split(".");
  let cur: any = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (cur[k] == null) cur[k] = isNaN(+keys[i + 1]) ? {} : [];
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
}

export function getPath(obj: any, path: string): any {
  const keys = path.split(".");
  let cur: any = obj;
  for (const k of keys) {
    if (cur == null) return undefined;
    cur = cur[k];
  }
  return cur;
}

/** Genera id único corto, exportado para módulos que lo usen (ej. picker). */
export function genId(prefix: string): string {
  return prefix + "_" + rid();
}

/**
 * Garantiza que cada item de un array tenga `id`. Si falta, lo genera
 * usando el prefix dado. Devuelve un nuevo array (immutable) si hubo cambios,
 * el mismo array si todos ya tenían id.
 *
 * Esto se usa para mantener `fieldPath` estable cuando se persisten estilos
 * por campo — sin id, el path tendría que usar índice (`items.2.name`) que
 * cambia al reordenar.
 */
export function ensureIds<T extends { id?: string }>(items: T[], prefix: string): T[] {
  let dirty = false;
  const out = items.map((it) => {
    if (it.id) return it;
    dirty = true;
    return { ...it, id: genId(prefix) };
  });
  return dirty ? out : items;
}

/** Macros de un DietFoodItem escalados según su `qty` vs `baseQty` (Feature 4). */
export function scaledItemMacros(item: DietFoodItem): {
  kcal: number;
  p: number;
  c: number;
  f: number;
} {
  const factor = item.baseQty > 0 ? item.qty / item.baseQty : 0;
  return {
    kcal: item.kcal * factor,
    p: item.protein * factor,
    c: item.carbs * factor,
    f: item.fat * factor,
  };
}

/** Suma (sin redondear) de los macros de una lista de DietFoodItem. */
export function sumMealMacros(items: DietFoodItem[]): {
  kcal: number;
  p: number;
  c: number;
  f: number;
} {
  return items.reduce(
    (acc, it) => {
      const m = scaledItemMacros(it);
      acc.kcal += m.kcal;
      acc.p += m.p;
      acc.c += m.c;
      acc.f += m.f;
      return acc;
    },
    { kcal: 0, p: 0, c: 0, f: 0 },
  );
}
